import React, { useCallback, useEffect, useRef, useState } from "react";
import { SelectButton } from "primereact/selectbutton";

import { Tariff } from "../../../../../types/tariff";
import { ContextLabels } from "../../../../../utils/tariff";

import { FormState, PeriodForm, TariffEditType, Weekday } from "./types";

import {
  getPriceWithVat,
  getTimeRestrictions,
  formatTimeWindow,
  initFormFromTariff,
  buildTariff,
  getCapData,
  validateHourlyForm,
  HourlyValidation,
} from "./utils";

import PeriodSection from "./components/PeriodSection";
import HourlySection from "./components/HourlySection";
import FreeSection from "./components/FreeSection";

import "./TariffEditor.css";

// ─── constants ────────────────────────────────────────────────────────────────

const TARIFF_TYPE_OPTIONS = [
  { label: "Static", value: "STATIC" },
  { label: "Day / Night", value: "DAY_NIGHT" },
  { label: "Hourly", value: "HOURLY" },
  { label: "Free", value: "FREE" },
];

// ─── main component ───────────────────────────────────────────────────────────

type Props = {
  tariff: Tariff;
  onChange: (tariff: Tariff) => void;
  onValidChange?: (valid: boolean) => void;
};

const TariffEditor: React.FC<Props> = ({ tariff, onChange, onValidChange }) => {
  const [form, setForm] = useState<FormState>(() => initFormFromTariff(tariff));
  const [hourlyValidation, setHourlyValidation] = useState<HourlyValidation>({
    valid: true,
    conflictingDays: [],
    windowErrors: [],
  });
  const isFirstRender = useRef(true);
  const originalTariff = useRef(tariff);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (form.type === "HOURLY") {
      const validation = validateHourlyForm(form.hourly.windows);
      setHourlyValidation(validation);
      onValidChange?.(validation.valid);
      if (!validation.valid) return;
    } else {
      setHourlyValidation((prev) =>
        prev.conflictingDays.length > 0 || prev.windowErrors.some(Boolean)
          ? { valid: true, conflictingDays: [], windowErrors: [] }
          : prev,
      );
      onValidChange?.(true);
    }

    onChange(buildTariff(form, originalTariff.current));
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTypeChange = (newType: TariffEditType) => {
    if (!newType || newType === form.type) return;
    setForm((prev) => {
      switch (newType) {
        case "DAY_NIGHT":
          return {
            ...prev,
            type: "DAY_NIGHT",
            day: { ...prev.static },
            night: { energyFee: 0, blockingFee: 0, blockingFeeAfterMin: 60 },
          };
        case "HOURLY":
          return {
            ...prev,
            type: "HOURLY",
            hourly: { ...prev.hourly, base: { ...prev.static } },
          };
        case "FREE":
          return { ...prev, type: "FREE" };
        default: // STATIC
          return {
            ...prev,
            type: "STATIC",
            // When coming from FREE, reset to minimum paid energy price
            static:
              prev.type === "HOURLY"
                ? { ...prev.hourly.base }
                : prev.type === "FREE"
                  ? { energyFee: 0.3, blockingFee: 0, blockingFeeAfterMin: 60 }
                  : { ...prev.day },
          };
      }
    });
  };

  const setPeriod = useCallback(
    (key: "static" | "day" | "night") => (updated: PeriodForm) =>
      setForm((prev) => ({ ...prev, [key]: updated })),
    [],
  );

  const activationPrice = getPriceWithVat(tariff, ContextLabels.activation);

  const staticCap = getCapData(
    tariff,
    ContextLabels.blockingFeeCap,
    ContextLabels.blockingFeeCapDay,
  );
  const dayCap = getCapData(
    tariff,
    ContextLabels.blockingFeeCapDay,
    ContextLabels.blockingFeeCap,
  );
  const nightCap = getCapData(
    tariff,
    ContextLabels.blockingFeeCapNight,
    ContextLabels.blockingFeeCap,
  );

  const dayTimeRestrictions = getTimeRestrictions(tariff, ContextLabels.enrgyPriceDay);
  const nightTimeRestrictions = getTimeRestrictions(tariff, ContextLabels.enrgyPriceNight);
  const dayPeriodLabel = dayTimeRestrictions
    ? formatTimeWindow(dayTimeRestrictions)
    : undefined;
  const nightPeriodLabel = nightTimeRestrictions
    ? formatTimeWindow(nightTimeRestrictions)
    : undefined;

  return (
    <div className="te-root">
      {/* Type toggle */}
      <SelectButton
        value={form.type}
        options={TARIFF_TYPE_OPTIONS}
        onChange={(e) => handleTypeChange(e.value)}
        allowEmpty={false}
        className="te-type-toggle"
      />

      {form.type === "STATIC" ? (
        <PeriodSection
          icon="pi pi-tag"
          label="Static"
          accentColor="#225349"
          period={form.static}
          cap={staticCap}
          activationPrice={activationPrice}
          onChange={setPeriod("static")}
        />
      ) : form.type === "DAY_NIGHT" ? (
        <div className="flex flex-column gap-3">
          <PeriodSection
            icon="pi pi-sun"
            label="Day"
            periodLabel={dayPeriodLabel}
            accentColor="#f59e0b"
            period={form.day}
            cap={dayCap}
            activationPrice={activationPrice}
            onChange={setPeriod("day")}
          />
          <PeriodSection
            icon="pi pi-moon"
            label="Night"
            periodLabel={nightPeriodLabel}
            accentColor="#6366f1"
            period={form.night}
            cap={nightCap}
            activationPrice={activationPrice}
            onChange={setPeriod("night")}
          />
        </div>
      ) : form.type === "HOURLY" ? (
        <HourlySection
          form={form.hourly}
          cap={staticCap}
          activationPrice={activationPrice}
          conflictingDays={hourlyValidation.conflictingDays as Weekday[]}
          windowErrors={hourlyValidation.windowErrors}
          onChange={(hourly) => setForm((prev) => ({ ...prev, hourly }))}
        />
      ) : (
        <FreeSection />
      )}
    </div>
  );
};

export default TariffEditor;
